import React, { useState } from 'react';
import { getErrorMessage } from '../utils/errorHandler';

interface AnnualUpdateSectionProps {
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

interface AnnualUpdateResult {
  target_year: number;
  new_fiscal_year: number;
  finalized_count: number;
  processed_assets: number;
  errors: string[];
}

const AnnualUpdateSection: React.FC<AnnualUpdateSectionProps> = ({ apiFetch }) => {
  const currentDate = new Date();
  const [targetYear, setTargetYear] = useState<number>(currentDate.getFullYear() - 1);
  const [newFiscalYear, setNewFiscalYear] = useState<number>(currentDate.getFullYear());
  const [processing, setProcessing] = useState<boolean>(false);
  const [result, setResult] = useState<AnnualUpdateResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExecute = async () => {
    if (
      !window.confirm(
        `${targetYear}年度（${targetYear}年4月1日〜${targetYear + 1}年3月31日）の年次更新処理を実行しますか？\n\nこの処理により、${targetYear}年度の減価償却明細が確定されます。`,
      )
    ) {
      return;
    }

    setProcessing(true);
    setError(null);
    setResult(null);

    try {
      const response = await apiFetch('/api/fixed-assets/annual-update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          target_year: targetYear,
          new_fiscal_year: newFiscalYear,
        }),
      });

      if (!response.ok) {
        const message = await getErrorMessage(response, '年次更新処理の実行に失敗しました。');
        throw new Error(message);
      }

      const data: AnnualUpdateResult = await response.json();
      setResult(data);
    } catch (err: any) {
      setError(err?.message || '年次更新処理の実行に失敗しました。');
    } finally {
      setProcessing(false);
    }
  };

  const years = Array.from({ length: 20 }, (_, i) => currentDate.getFullYear() - 10 + i);

  return (
    <div
      style={{
        border: '1px solid #d0d0d0',
        borderRadius: '4px',
        padding: '12px',
        backgroundColor: '#f9f9f9',
      }}
    >
      <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#003366' }}>
        年次更新処理
      </div>
      <div style={{ fontSize: '10px', color: '#666', marginBottom: '12px' }}>
        指定した年度の減価償却明細を確定します。年度切り替え時（例: 3月末）に実行してください。
      </div>

      <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
          対象年度:
          <select
            value={targetYear}
            onChange={(e) => setTargetYear(Number(e.target.value))}
            disabled={processing}
            style={{
              padding: '4px',
              fontSize: '11px',
              border: '1px inset #d0d0d0',
              backgroundColor: processing ? '#f5f5f5' : 'white',
            }}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}年度
              </option>
            ))}
          </select>
        </label>

        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px' }}>
          新年度:
          <select
            value={newFiscalYear}
            onChange={(e) => setNewFiscalYear(Number(e.target.value))}
            disabled={processing}
            style={{
              padding: '4px',
              fontSize: '11px',
              border: '1px inset #d0d0d0',
              backgroundColor: processing ? '#f5f5f5' : 'white',
            }}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}年度
              </option>
            ))}
          </select>
        </label>

        <button
          onClick={handleExecute}
          disabled={processing}
          style={{
            padding: '6px 12px',
            fontSize: '11px',
            backgroundColor: processing ? '#ccc' : '#003366',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: processing ? 'not-allowed' : 'pointer',
          }}
        >
          {processing ? '処理中...' : '実行'}
        </button>
      </div>

      {error && (
        <div
          style={{
            backgroundColor: '#ffebee',
            border: '1px solid #c62828',
            color: '#c62828',
            padding: '8px',
            fontSize: '11px',
            marginBottom: '8px',
            borderRadius: '4px',
          }}
        >
          ❌ {error}
        </div>
      )}

      {result && (
        <div
          style={{
            backgroundColor: '#e8f5e9',
            border: '1px solid #2e7d32',
            padding: '12px',
            fontSize: '11px',
            borderRadius: '4px',
          }}
        >
          <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#2e7d32' }}>
            ✅ 年次更新処理が完了しました
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px', marginBottom: '8px' }}>
            <div>対象年度: {result.target_year}年度</div>
            <div>新年度: {result.new_fiscal_year}年度</div>
            <div>確定件数: {result.finalized_count}件</div>
            <div>処理対象資産数: {result.processed_assets}件</div>
          </div>
          {result.errors.length > 0 && (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #2e7d32' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>エラー詳細:</div>
              <ul style={{ margin: 0, paddingLeft: '20px' }}>
                {result.errors.map((err, idx) => (
                  <li key={idx} style={{ fontSize: '10px', color: '#c62828' }}>
                    {err}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AnnualUpdateSection;

